import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import PaystackAnomalyDetectionPanel from './PaystackAnomalyDetectionPanel';

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d)?.toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatAmount = (n, currency = 'XOF') => {
  if (n == null) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency, maximumFractionDigits: 0 })?.format(n);
};

// ─── Status configs ───────────────────────────────────────────────────────────
const WEBHOOK_STATUS = {
  failed: { label: 'Échec', color: 'bg-red-100 text-red-700 border-red-200', icon: 'XCircle' },
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: 'Clock' },
  processed: { label: 'Traité', color: 'bg-green-100 text-green-700 border-green-200', icon: 'CheckCircle' },
  ignored: { label: 'Ignoré', color: 'bg-gray-100 text-gray-600 border-gray-200', icon: 'MinusCircle' },
  retrying: { label: 'Nouvelle tentative', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: 'RefreshCw' },
};

const MISMATCH_TYPES = {
  amount_mismatch: { label: 'Divergence montant', color: 'bg-rose-100 text-rose-700 border-rose-200', icon: 'DollarSign' },
  status_mismatch: { label: 'Divergence statut', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: 'AlertTriangle' },
  missing_in_db: { label: 'Absent en BDD', color: 'bg-red-100 text-red-700 border-red-200', icon: 'Database' },
  missing_in_paystack: { label: 'Absent Paystack', color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: 'CreditCard' },
  duplicate: { label: 'Doublon', color: 'bg-purple-100 text-purple-700 border-purple-200', icon: 'Copy' },
};

const StatusBadge = ({ status, config }) => {
  const cfg = config?.[status] || { label: status, color: 'bg-gray-100 text-gray-600 border-gray-200', icon: 'Circle' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg?.color}`}>
      <Icon name={cfg?.icon} size={11} />
      {cfg?.label}
    </span>
  );
};

const StatCard = ({ icon, label, value, color, sub, onClick }) => (
  <button
    onClick={onClick}
    className={`bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4 text-left hover:shadow-md transition-all w-full ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
  >
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
      <Icon name={icon} size={20} />
    </div>
    <div>
      <p className="text-sm text-gray-500 font-medium">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-0.5">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </button>
);

// ─── Webhook Sync Failures ────────────────────────────────────────────────────
const WebhookSyncFailures = () => {
  const [failures, setFailures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const loadFailures = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        ?.from('paystack_webhook_events')
        ?.select('*')
        ?.in('processing_status', ['failed', 'pending'])
        ?.order('created_at', { ascending: false })
        ?.limit(50);
      setFailures(data || []);
    } catch (err) {
      console.error('Error loading webhook failures:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadFailures(); }, [loadFailures]);

  const handleRetry = async (event) => {
    setRetrying(event?.id);
    try {
      await supabase
        ?.from('paystack_webhook_events')
        ?.update({ processing_status: 'pending', retry_count: (event?.retry_count || 0) + 1, last_retry_at: new Date()?.toISOString() })
        ?.eq('id', event?.id);
      await loadFailures();
    } catch (err) {
      console.error('Error retrying webhook:', err);
    } finally {
      setRetrying(null);
    }
  };

  const handleEscalate = async (event) => {
    try {
      await supabase?.from('paystack_webhook_events')?.update({ escalated: true, escalated_at: new Date()?.toISOString() })?.eq('id', event?.id);
      // Insert escalation notification
      await supabase?.from('notifications')?.insert({
        title: 'Escalade Webhook — Intervention requise',
        message: `Webhook ${event?.event_type} (${event?.paystack_reference || event?.id}) a échoué ${event?.retry_count || 0} fois et nécessite une intervention manuelle.`,
        type: 'admin_alert',
        priority: 'high',
      });
      await loadFailures();
    } catch (err) {
      console.error('Error escalating webhook:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">Échecs de Synchronisation Webhook</h4>
          <p className="text-xs text-gray-500 mt-0.5">{failures?.length} événement{failures?.length !== 1 ? 's' : ''} en échec ou en attente</p>
        </div>
        <button onClick={loadFailures} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
          <Icon name="RefreshCw" size={12} />
          Actualiser
        </button>
      </div>

      {failures?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 gap-3 bg-green-50 rounded-xl border border-green-200">
          <Icon name="CheckCircle" size={32} className="text-green-500" />
          <p className="text-sm font-medium text-green-700">Aucun échec de synchronisation</p>
          <p className="text-xs text-green-600">Tous les webhooks sont traités correctement</p>
        </div>
      ) : (
        <div className="space-y-2">
          {failures?.map(event => (
            <div key={event?.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div
                className="flex items-center gap-4 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedId(expandedId === event?.id ? null : event?.id)}
              >
                <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                  <Icon name="Webhook" size={16} className="text-red-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-gray-900 truncate">{event?.event_type || 'Événement inconnu'}</p>
                    <StatusBadge status={event?.processing_status} config={WEBHOOK_STATUS} />
                    {event?.escalated && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700 border border-red-200">
                        <Icon name="AlertOctagon" size={9} />
                        Escaladé
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <p className="text-xs text-gray-400 font-mono">{event?.paystack_reference || event?.id?.slice(0, 8)}</p>
                    <span className="text-gray-300">·</span>
                    <p className="text-xs text-gray-400">{formatDate(event?.created_at)}</p>
                    {event?.retry_count > 0 && (
                      <>
                        <span className="text-gray-300">·</span>
                        <p className="text-xs text-amber-600 font-medium">{event?.retry_count} tentative{event?.retry_count > 1 ? 's' : ''}</p>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={(e) => { e?.stopPropagation(); handleRetry(event); }}
                    disabled={retrying === event?.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors border border-blue-200 disabled:opacity-50"
                  >
                    <Icon name={retrying === event?.id ? 'Loader2' : 'RefreshCw'} size={12} className={retrying === event?.id ? 'animate-spin' : ''} />
                    Réessayer
                  </button>
                  {!event?.escalated && (event?.retry_count || 0) >= 2 && (
                    <button
                      onClick={(e) => { e?.stopPropagation(); handleEscalate(event); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-200"
                    >
                      <Icon name="AlertOctagon" size={12} />
                      Escalader
                    </button>
                  )}
                  <Icon name={expandedId === event?.id ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-gray-400" />
                </div>
              </div>

              {expandedId === event?.id && (
                <div className="border-t border-gray-100 px-4 py-4 bg-gray-50 space-y-3">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                    {[
                      { label: 'ID Événement', value: event?.id },
                      { label: 'Référence Paystack', value: event?.paystack_reference || '—' },
                      { label: 'Montant', value: formatAmount(event?.amount, event?.currency) },
                      { label: 'Dernière tentative', value: formatDate(event?.last_retry_at) },
                      { label: 'Créé le', value: formatDate(event?.created_at) },
                      { label: 'Escaladé', value: event?.escalated ? formatDate(event?.escalated_at) : 'Non' },
                    ]?.map(item => (
                      <div key={item?.label}>
                        <p className="text-gray-400 font-medium">{item?.label}</p>
                        <p className="text-gray-700 font-mono text-[11px] mt-0.5 break-all">{item?.value}</p>
                      </div>
                    ))}
                  </div>
                  {event?.error_message && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-xs font-semibold text-red-700 mb-1">Message d'erreur</p>
                      <p className="text-xs text-red-600 font-mono">{event?.error_message}</p>
                    </div>
                  )}
                  {event?.raw_payload && (
                    <details className="group">
                      <summary className="text-xs font-medium text-gray-500 cursor-pointer hover:text-gray-700">Payload brut</summary>
                      <pre className="mt-2 p-3 bg-gray-900 text-green-400 rounded-lg text-[10px] overflow-x-auto max-h-40">
                        {JSON.stringify(event?.raw_payload, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Payment Mismatches ───────────────────────────────────────────────────────
const PaymentMismatches = () => {
  const [mismatches, setMismatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const loadMismatches = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        ?.from('paystack_verification_logs')
        ?.select('*')
        ?.in('verification_status', ['mismatched', 'missing_in_db', 'missing_in_paystack', 'disputed'])
        ?.order('created_at', { ascending: false })
        ?.limit(50);

      if (filter !== 'all') {
        query = supabase
          ?.from('paystack_verification_logs')
          ?.select('*')
          ?.eq('verification_status', filter)
          ?.order('created_at', { ascending: false })
          ?.limit(50);
      }

      const { data } = await query;
      setMismatches(data || []);
    } catch (err) {
      console.error('Error loading mismatches:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { loadMismatches(); }, [loadMismatches]);

  const filterOptions = [
    { value: 'all', label: 'Tous' },
    { value: 'mismatched', label: 'Divergences' },
    { value: 'missing_in_db', label: 'Absents BDD' },
    { value: 'missing_in_paystack', label: 'Absents Paystack' },
    { value: 'disputed', label: 'Litiges' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">Divergences de Paiement</h4>
          <p className="text-xs text-gray-500 mt-0.5">{mismatches?.length} divergence{mismatches?.length !== 1 ? 's' : ''} détectée{mismatches?.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {filterOptions?.map(opt => (
            <button
              key={opt?.value}
              onClick={() => setFilter(opt?.value)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                filter === opt?.value ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {opt?.label}
            </button>
          ))}
        </div>
      </div>

      {mismatches?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 gap-3 bg-green-50 rounded-xl border border-green-200">
          <Icon name="CheckCircle" size={32} className="text-green-500" />
          <p className="text-sm font-medium text-green-700">Aucune divergence détectée</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Référence</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Type</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Paystack</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">BDD</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Statut</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {mismatches?.map(m => {
                const amountMismatch = m?.paystack_amount !== m?.db_amount;
                const statusMismatch = m?.paystack_status?.toLowerCase() !== m?.db_status?.toLowerCase();
                const mismatchType = m?.verification_status;
                const cfg = MISMATCH_TYPES?.[mismatchType] || MISMATCH_TYPES?.amount_mismatch;
                return (
                  <tr key={m?.id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4">
                      <p className="font-mono text-xs text-gray-700">{m?.paystack_reference || m?.id?.slice(0, 8)}</p>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cfg?.color}`}>
                        <Icon name={cfg?.icon} size={9} />
                        {cfg?.label}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <p className={`text-xs font-mono ${amountMismatch ? 'text-red-600 font-semibold' : 'text-gray-700'}`}>
                        {formatAmount(m?.paystack_amount, m?.paystack_currency)}
                      </p>
                      {statusMismatch && <p className="text-[10px] text-red-500 mt-0.5">{m?.paystack_status}</p>}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <p className={`text-xs font-mono ${amountMismatch ? 'text-red-600 font-semibold' : 'text-gray-700'}`}>
                        {formatAmount(m?.db_amount, m?.db_currency)}
                      </p>
                      {statusMismatch && <p className="text-[10px] text-red-500 mt-0.5">{m?.db_status}</p>}
                    </td>
                    <td className="py-3 px-4">
                      <StatusBadge status={m?.verification_status} config={{
                        mismatched: { label: 'Divergence', color: 'bg-red-100 text-red-700 border-red-200', icon: 'XCircle' },
                        missing_in_db: { label: 'Absent BDD', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: 'Database' },
                        missing_in_paystack: { label: 'Absent PS', color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: 'CreditCard' },
                        disputed: { label: 'Litige', color: 'bg-rose-100 text-rose-700 border-rose-200', icon: 'Flag' },
                      }} />
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-xs text-gray-400">{formatDate(m?.created_at)}</p>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Reconciliation Errors ────────────────────────────────────────────────────
const ReconciliationErrors = () => {
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retryHistory, setRetryHistory] = useState([]);

  const loadErrors = useCallback(async () => {
    setLoading(true);
    try {
      const [errorsRes, retryRes] = await Promise.all([
        supabase
          ?.from('paystack_verification_logs')
          ?.select('*')
          ?.eq('verification_status', 'mismatched')
          ?.eq('dispute_flag', false)
          ?.order('created_at', { ascending: false })
          ?.limit(30),
        supabase
          ?.from('paystack_webhook_events')
          ?.select('*')
          ?.gt('retry_count', 0)
          ?.order('last_retry_at', { ascending: false })
          ?.limit(20),
      ]);
      setErrors(errorsRes?.data || []);
      setRetryHistory(retryRes?.data || []);
    } catch (err) {
      console.error('Error loading reconciliation errors:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadErrors(); }, [loadErrors]);

  const handleAutoEscalate = async () => {
    const criticalErrors = errors?.filter(e => {
      const diff = Math?.abs((e?.paystack_amount || 0) - (e?.db_amount || 0));
      return diff > 10000;
    });

    if (criticalErrors?.length === 0) {
      alert('Aucune erreur critique nécessitant une escalade automatique.');
      return;
    }

    try {
      for (const err of criticalErrors) {
        await supabase?.from('notifications')?.insert({
          title: `Réconciliation critique — ${err?.paystack_reference}`,
          message: `Divergence de ${formatAmount(Math?.abs((err?.paystack_amount || 0) - (err?.db_amount || 0)))} détectée. Intervention technique requise.`,
          type: 'admin_alert',
          priority: 'critical',
        });
      }
      alert(`${criticalErrors?.length} erreur(s) critique(s) escaladée(s) à l'équipe technique.`);
    } catch (e) {
      console.error('Error auto-escalating:', e);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Reconciliation errors */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold text-gray-900">Erreurs de Réconciliation Paystack</h4>
            <p className="text-xs text-gray-500 mt-0.5">{errors?.length} erreur{errors?.length !== 1 ? 's' : ''} de réconciliation</p>
          </div>
          <button
            onClick={handleAutoEscalate}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-medium transition-colors"
          >
            <Icon name="AlertOctagon" size={13} />
            Escalade auto vers tech
          </button>
        </div>

        {errors?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 gap-2 bg-green-50 rounded-xl border border-green-200">
            <Icon name="CheckCircle" size={28} className="text-green-500" />
            <p className="text-sm font-medium text-green-700">Aucune erreur de réconciliation</p>
          </div>
        ) : (
          <div className="space-y-2">
            {errors?.map(err => {
              const diff = Math?.abs((err?.paystack_amount || 0) - (err?.db_amount || 0));
              const isCritical = diff > 10000;
              return (
                <div key={err?.id} className={`p-4 rounded-xl border ${isCritical ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isCritical ? 'bg-red-100' : 'bg-orange-100'}`}>
                        <Icon name={isCritical ? 'AlertOctagon' : 'AlertTriangle'} size={16} className={isCritical ? 'text-red-600' : 'text-orange-600'} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-gray-900 font-mono">{err?.paystack_reference || err?.id?.slice(0, 12)}</p>
                          {isCritical && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 border border-red-200">CRITIQUE</span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                          <span>Paystack: <strong className="text-gray-700">{formatAmount(err?.paystack_amount, err?.paystack_currency)}</strong></span>
                          <span>BDD: <strong className="text-gray-700">{formatAmount(err?.db_amount, err?.db_currency)}</strong></span>
                          <span className={`font-semibold ${isCritical ? 'text-red-600' : 'text-orange-600'}`}>Δ {formatAmount(diff)}</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 flex-shrink-0">{formatDate(err?.created_at)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Retry history */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-gray-900">Historique des Tentatives</h4>
        {retryHistory?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 gap-2 bg-gray-50 rounded-xl border border-gray-200">
            <Icon name="History" size={28} className="text-gray-400" />
            <p className="text-sm text-gray-500">Aucune tentative de réessai enregistrée</p>
          </div>
        ) : (
          <div className="space-y-2">
            {retryHistory?.map(event => (
              <div key={event?.id} className="flex items-center gap-4 p-3 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Icon name="RefreshCw" size={14} className="text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{event?.event_type}</p>
                  <p className="text-xs text-gray-400 font-mono">{event?.paystack_reference || event?.id?.slice(0, 8)}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs font-semibold text-blue-600">{event?.retry_count} tentative{event?.retry_count > 1 ? 's' : ''}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">{formatDate(event?.last_retry_at)}</p>
                </div>
                <StatusBadge status={event?.processing_status} config={WEBHOOK_STATUS} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Webhook Retrigger & Payload Inspector ────────────────────────────────────
const WebhookRetriggerInspector = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [retriggerLoading, setRetriggerLoading] = useState(null);
  const [dbSyncLoading, setDbSyncLoading] = useState(null);
  const [dbSyncResult, setDbSyncResult] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchRef, setSearchRef] = useState('');
  const [retriggerLog, setRetriggerLog] = useState([]);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        ?.from('paystack_webhook_events')
        ?.select('*')
        ?.order('created_at', { ascending: false })
        ?.limit(80);

      if (filterStatus !== 'all') {
        query = query?.eq('processing_status', filterStatus);
      }

      const { data } = await query;
      let items = data || [];

      if (searchRef?.trim()) {
        items = items?.filter(e =>
          e?.paystack_reference?.toLowerCase()?.includes(searchRef?.toLowerCase()) ||
          e?.event_type?.toLowerCase()?.includes(searchRef?.toLowerCase()) ||
          e?.id?.toLowerCase()?.includes(searchRef?.toLowerCase())
        );
      }

      setEvents(items);
    } catch (err) {
      console.error('Error loading webhook events:', err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, searchRef]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const handleRetrigger = async (event) => {
    setRetriggerLoading(event?.id);
    const startTime = Date.now();
    try {
      // Update status to retrying
      await supabase
        ?.from('paystack_webhook_events')
        ?.update({
          processing_status: 'retrying',
          retry_count: (event?.retry_count || 0) + 1,
          last_retry_at: new Date()?.toISOString(),
          error_message: null,
        })
        ?.eq('id', event?.id);

      // Simulate processing delay then mark as pending for re-processing
      await new Promise(r => setTimeout(r, 800));

      await supabase
        ?.from('paystack_webhook_events')
        ?.update({ processing_status: 'pending' })
        ?.eq('id', event?.id);

      const elapsed = Date.now() - startTime;
      setRetriggerLog(prev => [{
        id: event?.id,
        reference: event?.paystack_reference || event?.id?.slice(0, 8),
        event_type: event?.event_type,
        status: 'success',
        elapsed_ms: elapsed,
        timestamp: new Date()?.toISOString(),
      }, ...prev?.slice(0, 9)]);

      await loadEvents();
    } catch (err) {
      setRetriggerLog(prev => [{
        id: event?.id,
        reference: event?.paystack_reference || event?.id?.slice(0, 8),
        event_type: event?.event_type,
        status: 'error',
        error: err?.message,
        timestamp: new Date()?.toISOString(),
      }, ...prev?.slice(0, 9)]);
    } finally {
      setRetriggerLoading(null);
    }
  };

  const handleDbSync = async (event) => {
    setDbSyncLoading(event?.id);
    setDbSyncResult(null);
    try {
      // Check if transaction exists in DB
      const checks = await Promise.all([
        supabase?.from('escrow_transactions')?.select('id, status, amount_total')?.eq('payment_intent_id', event?.paystack_reference)?.maybeSingle(),
        supabase?.from('paystack_verification_logs')?.select('id, verification_status, paystack_amount, db_amount')?.eq('paystack_reference', event?.paystack_reference)?.maybeSingle(),
      ]);

      const [txCheck, verifyCheck] = checks;
      const result = {
        event_id: event?.id,
        reference: event?.paystack_reference,
        event_type: event?.event_type,
        webhook_status: event?.processing_status,
        db_transaction: txCheck?.data ? {
          found: true,
          id: txCheck?.data?.id,
          status: txCheck?.data?.status,
          amount: txCheck?.data?.amount_total,
        } : { found: false },
        verification_log: verifyCheck?.data ? {
          found: true,
          verification_status: verifyCheck?.data?.verification_status,
          paystack_amount: verifyCheck?.data?.paystack_amount,
          db_amount: verifyCheck?.data?.db_amount,
          synced: verifyCheck?.data?.verification_status === 'verified',
        } : { found: false },
        synced_at: new Date()?.toISOString(),
      };

      setDbSyncResult(result);
      setSelectedEvent(event);
    } catch (err) {
      setDbSyncResult({ error: err?.message });
    } finally {
      setDbSyncLoading(null);
    }
  };

  const statusFilters = [
    { value: 'all', label: 'Tous' },
    { value: 'failed', label: 'Échecs' },
    { value: 'pending', label: 'En attente' },
    { value: 'processed', label: 'Traités' },
    { value: 'retrying', label: 'En cours' },
  ];

  return (
    <div className="space-y-5">
      {/* Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px] relative">
          <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchRef}
            onChange={(e) => setSearchRef(e?.target?.value)}
            placeholder="Rechercher par référence ou type..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {statusFilters?.map(f => (
            <button
              key={f?.value}
              onClick={() => setFilterStatus(f?.value)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                filterStatus === f?.value ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f?.label}
            </button>
          ))}
        </div>
        <button
          onClick={loadEvents}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <Icon name="RefreshCw" size={12} />
          Actualiser
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Events list */}
        <div className="lg:col-span-3 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {events?.length} événement{events?.length !== 1 ? 's' : ''}
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-7 h-7 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
            </div>
          ) : events?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 gap-2 bg-gray-50 rounded-xl border border-gray-200">
              <Icon name="Inbox" size={28} className="text-gray-400" />
              <p className="text-sm text-gray-500">Aucun événement trouvé</p>
            </div>
          ) : (
            <div className="space-y-1.5 max-h-[500px] overflow-y-auto pr-1">
              {events?.map(event => {
                const statusCfg = WEBHOOK_STATUS?.[event?.processing_status] || WEBHOOK_STATUS?.pending;
                const isSelected = selectedEvent?.id === event?.id;
                return (
                  <div
                    key={event?.id}
                    className={`bg-white border rounded-xl p-3 transition-all ${isSelected ? 'border-amber-400 ring-1 ring-amber-300' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <Icon name="Webhook" size={14} className="text-gray-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-xs font-semibold text-gray-900 truncate">{event?.event_type || 'Événement'}</p>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${statusCfg?.color}`}>
                            <Icon name={statusCfg?.icon} size={9} />
                            {statusCfg?.label}
                          </span>
                        </div>
                        <p className="text-[11px] text-gray-400 font-mono mt-0.5 truncate">{event?.paystack_reference || event?.id?.slice(0, 12)}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{formatDate(event?.created_at)}</p>
                      </div>
                      <div className="flex flex-col gap-1.5 flex-shrink-0">
                        <button
                          onClick={() => handleRetrigger(event)}
                          disabled={retriggerLoading === event?.id || event?.processing_status === 'processed'}
                          title="Re-déclencher le webhook"
                          className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Icon name={retriggerLoading === event?.id ? 'Loader2' : 'Play'} size={11} className={retriggerLoading === event?.id ? 'animate-spin' : ''} />
                          Retrigger
                        </button>
                        <button
                          onClick={() => handleDbSync(event)}
                          disabled={dbSyncLoading === event?.id}
                          title="Vérifier la synchronisation BDD"
                          className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg border border-emerald-200 transition-colors disabled:opacity-40"
                        >
                          <Icon name={dbSyncLoading === event?.id ? 'Loader2' : 'Database'} size={11} className={dbSyncLoading === event?.id ? 'animate-spin' : ''} />
                          Sync BDD
                        </button>
                      </div>
                    </div>

                    {/* Payload preview on click */}
                    {isSelected && event?.raw_payload && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Payload</p>
                        <pre className="p-2.5 bg-gray-900 text-green-400 rounded-lg text-[10px] overflow-x-auto max-h-28">
                          {JSON.stringify(event?.raw_payload, null, 2)}
                        </pre>
                      </div>
                    )}

                    {event?.error_message && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-[10px] text-red-600 font-mono">{event?.error_message}</p>
                      </div>
                    )}

                    <button
                      onClick={() => setSelectedEvent(isSelected ? null : event)}
                      className="mt-2 text-[10px] text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-1"
                    >
                      <Icon name={isSelected ? 'ChevronUp' : 'ChevronDown'} size={11} />
                      {isSelected ? 'Masquer payload' : 'Inspecter payload'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right panel: DB Sync result + Retrigger log */}
        <div className="lg:col-span-2 space-y-4">
          {/* DB Sync Result */}
          {dbSyncResult && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Icon name="Database" size={16} className="text-emerald-600" />
                <h5 className="text-sm font-bold text-gray-900">Vérification Sync BDD</h5>
              </div>

              {dbSyncResult?.error ? (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-xs text-red-600">{dbSyncResult?.error}</p>
                </div>
              ) : (
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
                    <span className="text-gray-500">Référence</span>
                    <span className="font-mono text-gray-700">{dbSyncResult?.reference || '—'}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
                    <span className="text-gray-500">Statut webhook</span>
                    <span className={`font-semibold ${dbSyncResult?.webhook_status === 'processed' ? 'text-green-600' : 'text-orange-600'}`}>
                      {dbSyncResult?.webhook_status}
                    </span>
                  </div>

                  {/* DB Transaction */}
                  <div className={`p-3 rounded-lg border ${dbSyncResult?.db_transaction?.found ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Icon name={dbSyncResult?.db_transaction?.found ? 'CheckCircle' : 'XCircle'} size={13} className={dbSyncResult?.db_transaction?.found ? 'text-green-600' : 'text-red-600'} />
                      <span className="font-semibold text-gray-700">Transaction en BDD</span>
                    </div>
                    {dbSyncResult?.db_transaction?.found ? (
                      <div className="space-y-0.5 text-[11px] text-gray-600">
                        <p>Statut: <strong>{dbSyncResult?.db_transaction?.status}</strong></p>
                        <p>Montant: <strong>{formatAmount(dbSyncResult?.db_transaction?.amount)}</strong></p>
                      </div>
                    ) : (
                      <p className="text-[11px] text-red-600">Transaction introuvable en base de données</p>
                    )}
                  </div>

                  {/* Verification log */}
                  <div className={`p-3 rounded-lg border ${dbSyncResult?.verification_log?.found ? (dbSyncResult?.verification_log?.synced ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200') : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Icon name={dbSyncResult?.verification_log?.synced ? 'CheckCircle' : 'AlertTriangle'} size={13} className={dbSyncResult?.verification_log?.synced ? 'text-green-600' : 'text-orange-600'} />
                      <span className="font-semibold text-gray-700">Log de vérification</span>
                    </div>
                    {dbSyncResult?.verification_log?.found ? (
                      <div className="space-y-0.5 text-[11px] text-gray-600">
                        <p>Statut: <strong>{dbSyncResult?.verification_log?.verification_status}</strong></p>
                        <p>Paystack: <strong>{formatAmount(dbSyncResult?.verification_log?.paystack_amount)}</strong></p>
                        <p>BDD: <strong>{formatAmount(dbSyncResult?.verification_log?.db_amount)}</strong></p>
                      </div>
                    ) : (
                      <p className="text-[11px] text-gray-500">Aucun log de vérification trouvé</p>
                    )}
                  </div>

                  <p className="text-[10px] text-gray-400">Vérifié le {formatDate(dbSyncResult?.synced_at)}</p>
                </div>
              )}
            </div>
          )}

          {/* Retrigger log */}
          {retriggerLog?.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Icon name="History" size={16} className="text-blue-600" />
                <h5 className="text-sm font-bold text-gray-900">Journal des Retriggers</h5>
              </div>
              <div className="space-y-1.5">
                {retriggerLog?.map((log, i) => (
                  <div key={i} className={`flex items-center gap-2 p-2 rounded-lg text-xs ${log?.status === 'success' ? 'bg-green-50' : 'bg-red-50'}`}>
                    <Icon name={log?.status === 'success' ? 'CheckCircle' : 'XCircle'} size={12} className={log?.status === 'success' ? 'text-green-600' : 'text-red-600'} />
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-gray-700 truncate">{log?.reference}</p>
                      <p className="text-gray-400 text-[10px]">{log?.event_type}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      {log?.elapsed_ms && <p className="text-gray-500 text-[10px]">{log?.elapsed_ms}ms</p>}
                      <p className="text-gray-400 text-[10px]">{formatDate(log?.timestamp)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!dbSyncResult && retriggerLog?.length === 0 && (
            <div className="flex flex-col items-center justify-center py-10 gap-3 bg-gray-50 rounded-xl border border-dashed border-gray-300">
              <Icon name="MousePointerClick" size={28} className="text-gray-400" />
              <p className="text-sm text-gray-500 text-center">
                Cliquez sur <strong>Retrigger</strong> ou <strong>Sync BDD</strong> pour inspecter un événement
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const FinanceOperationsTab = () => {
  const [activeSection, setActiveSection] = useState('webhooks');
  const [stats, setStats] = useState({ webhookFailures: 0, paymentMismatches: 0, reconciliationErrors: 0, retryCount: 0, anomalies: 0 });
  const channelRef = useRef(null);

  const loadStats = useCallback(async () => {
    try {
      const [wh, mm, re, rt, an] = await Promise.all([
        supabase?.from('paystack_webhook_events')?.select('*', { count: 'exact', head: true })?.in('processing_status', ['failed', 'pending']),
        supabase?.from('paystack_verification_logs')?.select('*', { count: 'exact', head: true })?.in('verification_status', ['mismatched', 'missing_in_db', 'missing_in_paystack']),
        supabase?.from('paystack_verification_logs')?.select('*', { count: 'exact', head: true })?.eq('verification_status', 'mismatched')?.eq('dispute_flag', false),
        supabase?.from('paystack_webhook_events')?.select('*', { count: 'exact', head: true })?.gt('retry_count', 0),
        supabase?.from('paystack_anomaly_detections')?.select('*', { count: 'exact', head: true })?.eq('dismissed', false),
      ]);
      setStats({
        webhookFailures: wh?.count || 0,
        paymentMismatches: mm?.count || 0,
        reconciliationErrors: re?.count || 0,
        retryCount: rt?.count || 0,
        anomalies: an?.count || 0,
      });
    } catch (err) {
      console.error('Error loading finance ops stats:', err);
    }
  }, []);

  useEffect(() => {
    loadStats();
    channelRef.current = supabase
      ?.channel('finance-ops-realtime')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'paystack_webhook_events' }, loadStats)
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'paystack_verification_logs' }, loadStats)
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'paystack_anomaly_detections' }, loadStats)
      ?.subscribe();
    return () => { supabase?.removeChannel(channelRef?.current); };
  }, [loadStats]);

  const sections = [
    { id: 'webhooks', label: 'Échecs Webhook', icon: 'Webhook', count: stats?.webhookFailures, countColor: 'bg-red-500' },
    { id: 'retrigger', label: 'Retrigger & Inspection', icon: 'Play', count: 0, countColor: 'bg-blue-500' },
    { id: 'anomalies', label: 'Anomalies Temps Réel', icon: 'AlertOctagon', count: stats?.anomalies, countColor: 'bg-red-600' },
    { id: 'mismatches', label: 'Divergences Paiement', icon: 'AlertTriangle', count: stats?.paymentMismatches, countColor: 'bg-orange-500' },
    { id: 'reconciliation', label: 'Réconciliation', icon: 'RefreshCw', count: stats?.reconciliationErrors, countColor: 'bg-rose-500' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Finance Operations</h3>
            <p className="text-sm text-gray-500 mt-0.5">Surveillance des opérations financières et synchronisation Paystack</p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-xs font-medium text-green-700">Temps réel actif</span>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <StatCard icon="Webhook" label="Échecs Webhook" value={stats?.webhookFailures} color="bg-red-100 text-red-600" sub="En attente de traitement" onClick={() => setActiveSection('webhooks')} />
          <StatCard icon="Play" label="Webhooks à Retrigger" value={stats?.webhookFailures} color="bg-blue-100 text-blue-600" sub="Retrigger manuel" onClick={() => setActiveSection('retrigger')} />
          <StatCard icon="AlertOctagon" label="Anomalies Actives" value={stats?.anomalies} color="bg-red-100 text-red-700" sub="Doublons & divergences" onClick={() => setActiveSection('anomalies')} />
          <StatCard icon="AlertTriangle" label="Divergences Paiement" value={stats?.paymentMismatches} color="bg-orange-100 text-orange-600" sub="Paystack vs BDD" onClick={() => setActiveSection('mismatches')} />
          <StatCard icon="RefreshCw" label="Tentatives de Réessai" value={stats?.retryCount} color="bg-blue-100 text-blue-600" sub="Webhooks relancés" onClick={() => setActiveSection('reconciliation')} />
        </div>
      </div>

      {/* Section tabs */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex border-b border-gray-200 overflow-x-auto">
          {sections?.map(section => (
            <button
              key={section?.id}
              onClick={() => setActiveSection(section?.id)}
              className={`flex-shrink-0 flex items-center justify-center gap-2 px-4 py-3.5 text-sm font-medium transition-colors relative ${
                activeSection === section?.id
                  ? 'text-amber-700 bg-amber-50 border-b-2 border-amber-500' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <Icon name={section?.icon} size={15} />
              <span className="hidden sm:inline">{section?.label}</span>
              {section?.count > 0 && (
                <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white ${section?.countColor}`}>
                  {section?.count > 99 ? '99+' : section?.count}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="p-6">
          {activeSection === 'webhooks' && <WebhookSyncFailures />}
          {activeSection === 'retrigger' && <WebhookRetriggerInspector />}
          {activeSection === 'anomalies' && <PaystackAnomalyDetectionPanel />}
          {activeSection === 'mismatches' && <PaymentMismatches />}
          {activeSection === 'reconciliation' && <ReconciliationErrors />}
        </div>
      </div>
    </div>
  );
};

export default FinanceOperationsTab;
